import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";

type Props = {
  open: boolean;
  busy?: boolean;
  error?: string | null;
  onClose: () => void;
  onSubmit: (question: string, options: string[]) => void;
};

const MIN_OPTS = 2;
const MAX_OPTS = 12;

export function ChatPollComposer({ open, busy = false, error = null, onClose, onSubmit }: Props) {
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState<string[]>(["", ""]);

  useEffect(() => {
    if (!open) return;
    setQuestion("");
    setOptions(["", ""]);
  }, [open]);

  if (!open) return null;

  const clean = options.map((o) => o.trim()).filter(Boolean);
  const canSend = question.trim().length > 0 && clean.length >= MIN_OPTS && !busy;

  return (
    <div
      className="chat-confirm-overlay fixed inset-0 z-[100] flex items-end justify-center p-3 sm:items-center sm:p-4"
      role="presentation"
    >
      <div
        className="chat-confirm-dialog w-full max-w-md overflow-hidden rounded-2xl border border-[var(--chat-border)] bg-[var(--chat-surface)] shadow-[var(--chat-shadow)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="chat-poll-composer-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-[var(--chat-border)] px-4 py-3">
          <h2 id="chat-poll-composer-title" className="text-sm font-semibold text-[var(--chat-text)]">
            Nouveau sondage
          </h2>
          <p className="mt-0.5 text-[11px] text-[var(--chat-muted)]">
            Une question, puis les réponses. Chacun clique son choix.
          </p>
        </div>
        <div className="grid max-h-[min(70vh,28rem)] gap-3 overflow-y-auto px-4 py-3">
          <label className="grid gap-1">
            <span className="text-[11px] font-medium text-[var(--chat-muted)]">Question</span>
            <input
              value={question}
              maxLength={280}
              autoFocus
              placeholder="Où on déjeune ?"
              className="h-10 rounded-xl border border-[var(--chat-border)] bg-[var(--chat-bg)] px-3 text-sm text-[var(--chat-text)] outline-none focus:border-[var(--chat-accent)]"
              onChange={(e) => setQuestion(e.target.value)}
            />
          </label>
          <div className="grid gap-2">
            <span className="text-[11px] font-medium text-[var(--chat-muted)]">Réponses</span>
            {options.map((opt, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  value={opt}
                  maxLength={120}
                  placeholder={`Réponse ${i + 1}`}
                  className="h-9 min-w-0 flex-1 rounded-xl border border-[var(--chat-border)] bg-[var(--chat-bg)] px-3 text-sm text-[var(--chat-text)] outline-none focus:border-[var(--chat-accent)]"
                  onChange={(e) => {
                    const next = [...options];
                    next[i] = e.target.value;
                    setOptions(next);
                  }}
                />
                {options.length > MIN_OPTS ? (
                  <button
                    type="button"
                    className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg text-[var(--chat-muted)] hover:bg-[var(--chat-surface-elevated)] hover:text-[var(--chat-danger)]"
                    aria-label="Retirer cette réponse"
                    onClick={() => setOptions(options.filter((_, j) => j !== i))}
                  >
                    <Trash2 className="size-4" />
                  </button>
                ) : null}
              </div>
            ))}
            {options.length < MAX_OPTS ? (
              <button
                type="button"
                className="inline-flex items-center gap-1.5 self-start rounded-lg px-1.5 py-1 text-xs font-medium text-[var(--chat-accent-strong)] hover:bg-[var(--chat-accent-soft)]"
                onClick={() => setOptions([...options, ""])}
              >
                <Plus className="size-3.5" />
                Ajouter une réponse
              </button>
            ) : null}
          </div>
          {error ? <p className="text-xs text-[var(--chat-danger)]">{error}</p> : null}
        </div>
        <div className="flex justify-end gap-2 border-t border-[var(--chat-border)] px-4 py-2.5">
          <button
            type="button"
            disabled={busy}
            className="rounded-xl px-4 py-2 text-sm font-medium text-[var(--chat-muted)] hover:bg-[var(--chat-surface-elevated)]"
            onClick={onClose}
          >
            Annuler
          </button>
          <button
            type="button"
            disabled={!canSend}
            className="rounded-xl bg-[var(--chat-accent)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            onClick={() => {
              if (!canSend) return;
              onSubmit(question.trim(), clean);
            }}
          >
            {busy ? "Envoi…" : "Envoyer"}
          </button>
        </div>
      </div>
    </div>
  );
}
